import React, {ReactElement} from 'react';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import {PROJECT_STATUS, WorkflowCategories} from '../../../../constants';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {selectedStageOption, stagePickerOptions} from '../../../../forecast-app/shared/util/projectStagePicker';
import {showsOpportunityStages} from '../../../../forecast-app/shared/util/opportunityStages';
import Util from '../../../../forecast-app/shared/util/util';
import UpdateProjectMutation from '../../../../mutations/update_project_mutation.modern';
import UpdateTaskMutation from '../../../../mutations/update_task_mutation.modern';
import {TASKS_OPTIONS} from '../../../modal/CloseProjectModal';
import {createToast} from '../../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {ButtonDropdownField, FlexRow, DropdownList, Text} from '@forecast-it/design-system';
import {DesignSystemDropdownOldLookAdapter} from '../../../../forecast-app/shared/components/dropdowns/DesignSystemDropdownOldLookAdapter';
import {updateProjectOnSuccess} from '../../../../forecast-app/project-tab/projects/project-settings/ProjectSettingsMutations';
import {StagePickerOption} from '../../../../forecast-app/shared/util/projectStagePicker';
import {AsRead} from '../../../../forecast-app/shared/util/AsRead';
import {ProjectStageDropdown_viewer$data} from './__generated__/ProjectStageDropdown_viewer.graphql';

type Viewer = AsRead<ProjectStageDropdown_viewer$data>;
type TaskEdge = Viewer['project']['tasks']['edges'][number];
// order and jiraStatusId are not in the fragment, so under Relay's data masking they are never set.
type ColumnNode = Viewer['project']['statusColumnsV2']['edges'][number]['node'] & {
	readonly order?: number;
	readonly jiraStatusId?: string;
};

type Props = {viewer: Viewer};

const changeStage = (projectId: string, stageId: string, updatedMessage: string): void => {
	const project = {id: projectId};

	Util.CommitSchedulingModalUpdate(
		UpdateProjectMutation,
		{
			project: project,
			stageId: stageId,
		},
		(res: Parameters<typeof updateProjectOnSuccess>[0]) => {
			updateProjectOnSuccess(res);
			createToast({duration: 3000, message: updatedMessage});
		}
	);
};

const changeTasksColumn = (notDoneIds: string[], statusColumnId: string, statusColumnCategory: string): void => {
	const chunkArray: string[][] = Util.chunkArray(notDoneIds, 100);
	chunkArray.forEach(chunkIds => {
		Util.CommitMutation(UpdateTaskMutation, {
			ids: chunkIds,
			statusColumnId: statusColumnId,
			statusColumnCategory: statusColumnCategory,
		});
	});
};

const ProjectStageDropdown = ({viewer}: Props): ReactElement => {
	const intl = useIntl();
	const stageUpdatedMessage = intl.formatMessage({id: 'settings.project_stage_updated'});

	const project = viewer.project;
	const projectId = project.id;
	const projectTasks = project.tasks.edges;
	const projectColumns = project.statusColumnsV2.edges;

	// A project already in Opportunity keeps it as an option, so its current stage shows even without access.
	const stages = stagePickerOptions(
		viewer.company.projectStages.edges.map(edge => edge.node),
		{
			showOpportunity: showsOpportunityStages(),
			currentStageId: project.stage?.id ?? null,
		},
		intl
	);

	const handleUnfinishedWork = (
		option: number,
		statusColumnId: string,
		notDoneTasks: ReadonlyArray<TaskEdge>,
		stageId: string
	): void => {
		switch (option) {
			case TASKS_OPTIONS.DONE: {
				const notDoneIds = notDoneTasks.map(task => task.node.id);
				changeTasksColumn(notDoneIds, statusColumnId, 'DONE');
				changeStage(projectId, stageId, stageUpdatedMessage);
				break;
			}
			case TASKS_OPTIONS.STAY:
				changeStage(projectId, stageId, stageUpdatedMessage);
				break;
			default:
				break;
		}
	};

	const handleChangeStage = (stage: StagePickerOption): void => {
		if (stage) {
			if (stage.profile === PROJECT_STATUS.DONE) {
				const notDoneTasks = projectTasks.filter(task => task.node.statusColumnV2.category !== PROJECT_STATUS.DONE);
				if (notDoneTasks.length > 0) {
					const doneColumns = projectColumns
						.filter(col => col.node.category === WorkflowCategories.DONE)
						.map(col => col.node as ColumnNode)
						.sort((a, b) => Number(a.order) - Number(b.order))
						.map(node => ({
							value: node.id,
							label: node.name,
							logo: node.jiraStatusId ? 'jira-logo' : undefined,
						}));
					showModal({
						type: MODAL_TYPE.CLOSE_PROJECT,
						tasksList: notDoneTasks,
						doneColumns: doneColumns,
						callback: (option: number, statucColumnId: string) =>
							handleUnfinishedWork(option, statucColumnId, notDoneTasks, stage.value),
					});
				} else {
					changeStage(projectId, stage.value, stageUpdatedMessage);
				}
			} else {
				changeStage(projectId, stage.value, stageUpdatedMessage);
			}
		}
	};

	return (
		<>
			<DesignSystemDropdownOldLookAdapter>
				<DropdownList>
					<DropdownList.Trigger data-cy={'settings-project-stage'}>
						<ButtonDropdownField>
							{project.stage ? (
								<Text>{selectedStageOption(stages, project.stage)?.label}</Text>
							) : (
								<Text color={Text.Color.auxiliary}>{intl.formatMessage({id: 'settings.project-stage'})}</Text>
							)}
						</ButtonDropdownField>
					</DropdownList.Trigger>
					<div style={{zIndex: 1000}}>
						<DropdownList.Content align={'start'}>
							{stages.map(stage => (
								<DropdownList.Item key={stage.value} onClick={() => handleChangeStage(stage)}>
									<FlexRow>
										<Text>{stage.label}</Text>
									</FlexRow>
								</DropdownList.Item>
							))}
						</DropdownList.Content>
					</div>
				</DropdownList>
			</DesignSystemDropdownOldLookAdapter>
		</>
	);
};

const ProjectStageDropdownQuery = graphql`
	query ProjectStageDropdown_Query($projectId: ID) {
		viewer {
			component(name: "project_stage__dropdown")
			actualPersonId
			id
			...ProjectStageDropdown_viewer @arguments(projectId: $projectId)
		}
	}
`;

export {ProjectStageDropdownQuery};

export default createFragmentContainer(ProjectStageDropdown, {
	viewer: graphql`
		fragment ProjectStageDropdown_viewer on Viewer @argumentDefinitions(projectId: {type: "ID"}) {
			company {
				id
				modules {
					moduleType
				}
				...ProjectStages_company @relay(mask: false)
			}
			project(internalId: $projectId) {
				id
				stage {
					id
					name
					profile
				}
				tasks(first: 100000) {
					edges {
						node {
							id
							name
							companyTaskId
							statusColumnV2 {
								name
								category
							}
						}
					}
				}
				statusColumnsV2(first: 1000000) {
					edges {
						node {
							id
							category
							name
						}
					}
				}
			}
		}
	`,
});
